import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useGA } from '~/hooks'
import styled from 'styled-components'
import IconChevronDown from '~/assets/images/chevron-down-black.svg'
import IconCTAArrow from '~/assets/images/cta-arrow-purple.svg'

interface FaqSectionProps {
  faqItems: { id: string; question: string; answer: string }[]
  from: 'stake' | 'wrap' | 'withdraw'
}

const StyledFaqSection = styled.div`
  width: 100%;
  max-width: 435px;
  margin: 5px auto 15px;

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textTitle};
    margin: 0 0 16px 0;
  }
`

const StyledFaqList = styled.div`
  padding: 24px 20px;
  border-radius: 16px;
  border: 1px solid #eeedf3;
  background: #fff;
`

const StyledFaqItem = styled.div<{ expanded?: boolean }>`
  padding: 24px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderSubtle};

  &:first-child {
    padding-top: 0;
  }
`

const StyledFaqQuestion = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  color: #222226;
  cursor: pointer;
`

const StyledFaqArrow = styled.div<{ expanded?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  transition: transform 0.2s ease;
  transform: ${({ expanded }) =>
    expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`

const StyledFaqAnswer = styled.div<{ expanded?: boolean }>`
  margin-top: ${({ expanded }) => (expanded ? '24px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease;
  max-height: ${({ expanded }) => (expanded ? '500px' : '0')};
  opacity: ${({ expanded }) => (expanded ? '1' : '0')};
`

const StyledFaqAnswerInner = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  text-align: justify;
  color: ${({ theme }) => theme.colors.textCaption};
  white-space: pre-line;
`

const StyledMoreDetails = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 16px 0 0 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 27px;
  color: #5f55a9;
  text-decoration: none;

  &:hover {
    color: #5f55a9;
  }

  img {
    width: 14px;
    height: 14px;
  }
`

const FaqSection: React.FC<FaqSectionProps> = ({ faqItems, from }) => {
  const { t } = useTranslation()
  const { trackClick } = useGA()
  const [expandedFaqs, setExpandedFaqs] = useState<Set<string>>(new Set())

  const moreDetailsLink = 'https://lido.fi/faq'

  const toggleFaq = useCallback(
    (faqId: string, faqQuestion: string, index: number) => {
      trackClick('webtl_stk_faq', {
        from,
        entryIndex: index + 1,
        title: faqQuestion,
      })

      setExpandedFaqs((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(faqId)) {
          newSet.delete(faqId)
        } else {
          newSet.add(faqId)
        }
        return newSet
      })
    },
    [],
  )

  return (
    <StyledFaqSection>
      <h3>FAQ</h3>
      <StyledFaqList>
        {faqItems.map((item, index) => (
          <StyledFaqItem key={item.id} expanded={expandedFaqs.has(item.id)}>
            <StyledFaqQuestion
              onClick={() => toggleFaq(item.id, item.question, index)}
            >
              {t(item.question)}
              <StyledFaqArrow expanded={expandedFaqs.has(item.id)}>
                <img
                  src={IconChevronDown}
                  width={16}
                  height={16}
                  alt="chevron down"
                />
              </StyledFaqArrow>
            </StyledFaqQuestion>
            <StyledFaqAnswer expanded={expandedFaqs.has(item.id)}>
              <StyledFaqAnswerInner>{t(item.answer)}</StyledFaqAnswerInner>
            </StyledFaqAnswer>
          </StyledFaqItem>
        ))}
        <StyledMoreDetails
          onClick={() => {
            trackClick('webtl_stk_faq_more', {
              from,
              url: moreDetailsLink,
            })
          }}
          href={moreDetailsLink}
          target="_blank"
          rel="noopener"
        >
          <span>{t('more_details')}</span>
          <img src={IconCTAArrow} alt="cta arrow" />
        </StyledMoreDetails>
      </StyledFaqList>
    </StyledFaqSection>
  )
}

export default FaqSection
